import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FiAlertCircle,
    FiEdit2,
    FiEye,
    FiPause,
    FiPlay,
    FiRotateCcw,
    FiSearch,
    FiShield,
    FiTrash2,
    FiUserPlus
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { adminApi, type AdminCustomer } from '../../services/admin';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';

type FilterStatus = 'all' | 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

export function AdminCustomers() {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState<AdminCustomer[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addForm, setAddForm] = useState({ name: '', email: '', password: '', whatsapp: '' });
    const [saving, setSaving] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminApi.listCustomers({
                status: filterStatus !== 'all' ? filterStatus : undefined,
                search: debouncedSearch || undefined,
            });
            setCustomers(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [filterStatus, debouncedSearch]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => { load(); }, [load]);

    const handleStatusToggle = async (customer: AdminCustomer) => {
        const next = customer.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        await adminApi.updateCustomerStatus(customer.id, next);
        load();
    };

    const handleSuspend = async (customer: AdminCustomer) => {
        if (!confirm(`Suspender ${customer.name}?`)) return;
        await adminApi.updateCustomerStatus(customer.id, 'SUSPENDED');
        load();
    };

    // Conta excluida: dono de empresa fica guardado (restauravel) e quem participava
    // da empresa dele sai so dela; convidado sem empresa propria sai de vez.
    const handleDelete = async (customer: AdminCustomer) => {
        if (customer.role === 'ADMIN') { alert('Não é possível excluir um admin.'); return; }
        if (customer.excluidoEm) {
            if (!confirm(`Excluir ${customer.name} DEFINITIVAMENTE? A conta não poderá mais ser restaurada.`)) return;
            await adminApi.deleteCustomerPermanently(customer.id);
            load();
            return;
        }
        const aviso = customer.tipo === 'convidado'
            ? `Excluir ${customer.name}? É convidado (sem empresa própria): a conta sai de vez.`
            : `Excluir ${customer.name}? A conta e a empresa ficam guardadas e podem ser restauradas aqui. Quem participa da empresa perde o acesso a ela.`;
        if (!confirm(aviso)) return;
        await adminApi.deleteCustomer(customer.id);
        load();
    };

    const handleRestore = async (customer: AdminCustomer) => {
        if (!confirm(`Restaurar ${customer.name}? A conta, a empresa e quem participava dela voltam como estavam.`)) return;
        try {
            const { participantesDeVolta } = await adminApi.restoreCustomer(customer.id);
            alert(`Conta restaurada. ${participantesDeVolta} participante(s) de volta à empresa.`);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Erro ao restaurar');
        }
        load();
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminApi.createCustomer(addForm);
            setIsAddModalOpen(false);
            setAddForm({ name: '', email: '', password: '', whatsapp: '' });
            load();
        } catch (err: any) {
            setAddError(err.message || 'Erro ao criar usuário');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Clientes</h2>
                    <p>Todos os usuários cadastrados</p>
                </div>
                <Button onClick={() => setIsAddModalOpen(true)}>
                    <FiUserPlus size={16} style={{ marginRight: '0.5rem' }} />
                    Novo Usuário
                </Button>
            </div>

            <div className={styles.filtersRow}>
                <div className={styles.searchInput}>
                    <FiSearch size={16} className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Buscar por nome ou email..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className={styles.filterButtons}>
                    {(['all', 'ACTIVE', 'INACTIVE', 'SUSPENDED'] as FilterStatus[]).map(s => (
                        <button
                            key={s}
                            className={`${styles.filterBtn} ${filterStatus === s ? styles.active : ''}`}
                            onClick={() => setFilterStatus(s)}
                        >
                            {s === 'all' ? 'Todos' : s === 'ACTIVE' ? 'Ativos' : s === 'INACTIVE' ? 'Inativos' : 'Suspensos'}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : (
                <div className={styles.usersSection}>
                    <table className={styles.usersTable}>
                        <thead>
                            <tr>
                                <th>Usuário</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Plano</th>
                                <th>Cadastro</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {customers.map(customer => (
                                <tr key={customer.id} className={customer.status !== 'ACTIVE' || customer.excluidoEm ? styles.inactiveRow : ''}>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div className={styles.userAvatar}>{customer.name.charAt(0).toUpperCase()}</div>
                                            <div className={styles.userDetails}>
                                                <div className={styles.name}>{customer.name}</div>
                                                <div className={styles.email}>{customer.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.roleBadge} ${customer.role === 'ADMIN' ? styles.admin : styles.user}`}>
                                            {customer.role === 'ADMIN' && <FiShield size={12} />}
                                            {customer.role === 'ADMIN' ? 'Admin' : customer.tipo === 'convidado' ? 'Convidado' : 'Cliente'}
                                        </span>
                                    </td>
                                    <td>
                                        {customer.excluidoEm ? (
                                            <span className={`${styles.statusBadge} ${styles.inactive}`} title={`Excluída em ${formatDate(customer.excluidoEm)}`}>
                                                Excluída
                                            </span>
                                        ) : (
                                            <span className={`${styles.statusBadge} ${styles[customer.status.toLowerCase()]}`}>
                                                {customer.status === 'ACTIVE' ? 'Ativo' : customer.status === 'INACTIVE' ? 'Inativo' : 'Suspenso'}
                                            </span>
                                        )}
                                    </td>
                                    <td>{formatDate(customer.createdAt)}</td>
                                    <td>
                                        {customer.subscription
                                            ? <span>{customer.subscription.planName}</span>
                                            : <span style={{ color: 'var(--color-gray-400)' }}>Sem plano</span>}
                                    </td>
                                    <td>
                                        <div className={styles.actionButtons}>
                                            <button className={styles.actionBtn} title="Ver detalhes" onClick={() => navigate(`/admin/clientes/${customer.id}`)}>
                                                <FiEye size={16} />
                                            </button>
                                            <button className={styles.actionBtn} title="Editar" onClick={() => navigate(`/admin/clientes/${customer.id}`)}>
                                                <FiEdit2 size={16} />
                                            </button>
                                            {customer.role !== 'ADMIN' && customer.excluidoEm && (
                                                <>
                                                    <button className={`${styles.actionBtn} ${styles.activateBtn}`} title="Restaurar" onClick={() => handleRestore(customer)}>
                                                        <FiRotateCcw size={16} />
                                                    </button>
                                                    <button className={`${styles.actionBtn} ${styles.deleteBtn}`} title="Excluir definitivamente" onClick={() => handleDelete(customer)}>
                                                        <FiTrash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                            {customer.role !== 'ADMIN' && !customer.excluidoEm && (
                                                <>
                                                    <button
                                                        className={`${styles.actionBtn} ${customer.status === 'ACTIVE' ? styles.deactivateBtn : styles.activateBtn}`}
                                                        title={customer.status === 'ACTIVE' ? 'Inativar' : 'Ativar'}
                                                        onClick={() => handleStatusToggle(customer)}
                                                    >
                                                        {customer.status === 'ACTIVE' ? <FiPause size={16} /> : <FiPlay size={16} />}
                                                    </button>
                                                    {customer.status !== 'SUSPENDED' && (
                                                        <button className={`${styles.actionBtn} ${styles.suspendBtn}`} title="Suspender" onClick={() => handleSuspend(customer)}>
                                                            <FiAlertCircle size={16} />
                                                        </button>
                                                    )}
                                                    <button className={`${styles.actionBtn} ${styles.deleteBtn}`} title="Excluir" onClick={() => handleDelete(customer)}>
                                                        <FiTrash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal isOpen={isAddModalOpen} onClose={() => { setIsAddModalOpen(false); setAddError(null); }} title="Novo Usuário" size="large">
                <form onSubmit={handleAdd}>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <Input label="Nome" value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} placeholder="Nome completo" />
                        </div>
                        <div className={styles.formGroup}>
                            <Input label="Email" type="email" value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} placeholder="email@exemplo.com" />
                        </div>
                        <div className={styles.formGroup}>
                            <Input label="WhatsApp" type="tel" value={addForm.whatsapp} onChange={e => setAddForm(p => ({ ...p, whatsapp: e.target.value.replace(/\D/g, '') }))} placeholder="5511999998888" />
                        </div>
                        <div className={styles.formGroup}>
                            <Input label="Senha inicial" type="password" value={addForm.password} onChange={e => setAddForm(p => ({ ...p, password: e.target.value }))} placeholder="Mínimo 6 caracteres" />
                        </div>
                    </div>
                    {addError && <p className={styles.formError}>{addError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setIsAddModalOpen(false); setAddError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Criar Usuário'}</Button>
                    </div>
                </form>
            </Modal>
        </AdminLayout>
    );
}
